'use client'

import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react'

import { todayInTimeZone, type DisplayDateAnchor } from '@/i18n/dates'

export interface WidgetState {
    from: string
    fromId: string
    to: string
    toId: string
    departDate: string
    returnDate?: string
    mode: 'bus' | 'train' | 'ferry' | 'flight'
    tripType: 'one-way' | 'round-trip'
    /** Seat count (1–8). Mirrors ui-react's multi-pax `?p=N` search param. */
    passengers: number
}

interface StoredWidgetState {
    state: WidgetState
    timestamp: number
}

const STORAGE_KEY = 'wanderu-widget-state'
const TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

const defaultState: WidgetState = {
    from: '',
    fromId: '',
    to: '',
    toId: '',
    departDate: '',
    mode: 'bus',
    tripType: 'one-way',
    passengers: 1,
}

/** Today (YYYY-MM-DD) in the browser's own local zone. Post-mount only — reading
 *  the ambient clock at render would break hydration (invariants.md#ssr-safe-rendered-dates). */
function getLocalToday(): string {
    const d = new Date()
    const y = String(d.getFullYear())
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

/**
 * Adjust dates so departDate is not in the past and returnDate >= departDate,
 * clamped against a caller-supplied `today`.
 *
 * `today` is **required but nullable** with **no ambient-clock fallback** — every
 * caller must pass it explicitly, so forgetting it is a compile error rather than a
 * silent no-clamp. The SSR / first-client-render path passes `anchor?.today` — a
 * fixed, server-computed string — which is `undefined` when a caller mounts the
 * widget without a `DisplayDateAnchor`. In that case we must NOT read the wall clock
 * at render: that is exactly the React #418 hydration hazard the anchor exists to
 * prevent (invariants.md#ssr-safe-rendered-dates), and a dropped `dateAnchor` prop
 * would otherwise silently reintroduce it. The post-mount effect always passes a
 * concrete `today` (origin- or browser-anchored) and does the clamp then, so a
 * missing anchor degrades to an un-clamped depart date the effect fills in —
 * never to a clock-dependent render.
 */
function adjustDates(state: WidgetState, today: string | undefined): WidgetState {
    if (today && (!state.departDate || state.departDate < today)) {
        state = { ...state, departDate: today }
    }

    if (state.returnDate && state.returnDate < state.departDate) {
        state = { ...state, returnDate: undefined }
    }

    return state
}

/** Read stored state from localStorage with TTL check */
function readStoredState(): WidgetState | null {
    if (typeof window === 'undefined') {
        return null
    }

    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
        return null
    }

    try {
        const parsed = JSON.parse(raw) as StoredWidgetState | WidgetState

        // Support both old format (plain state) and new format (with timestamp)
        if ('timestamp' in parsed && 'state' in parsed) {
            if (Date.now() - parsed.timestamp > TTL_MS) {
                localStorage.removeItem(STORAGE_KEY)
                return null
            }
            return parsed.state
        }

        // Legacy format: plain state without timestamp — treat as valid but migrate
        return parsed
    } catch {
        localStorage.removeItem(STORAGE_KEY)
        return null
    }
}

/** Enforce returnDate >= departDate on every state update */
function enforceReturnDate(state: WidgetState): WidgetState {
    if (state.returnDate && state.departDate && state.returnDate < state.departDate) {
        return { ...state, returnDate: state.departDate }
    }
    return state
}

/**
 * Drop keys whose value is `undefined` so a partial object passed by a caller
 * (e.g. `{ from: undefined }` from a widget mounted with no initial values)
 * doesn't clobber the string defaults during a spread merge.
 */
function definedValues<T extends object>(obj: T | undefined): Partial<T> {
    if (!obj) {
        return {}
    }
    return Object.fromEntries(
        Object.entries(obj).filter(([, value]) => value !== undefined),
    ) as Partial<T>
}

/** Field-wise equality, so the post-mount re-anchor can skip a no-op re-render. */
function widgetStateEqual(a: WidgetState, b: WidgetState): boolean {
    return (
        a.from === b.from &&
        a.fromId === b.fromId &&
        a.to === b.to &&
        a.toId === b.toId &&
        a.departDate === b.departDate &&
        a.returnDate === b.returnDate &&
        a.mode === b.mode &&
        a.tripType === b.tripType &&
        a.passengers === b.passengers
    )
}

/**
 * The client's effective "today" once hydrated (invariants.md#ssr-safe-rendered-dates):
 *  - a known origin zone wins on both server and client, so the depart-date stays
 *    pinned to the origin and never drifts to the browser clock (rule 3);
 *  - with no origin, the user's own local day wins (rule 4).
 */
function clientToday(anchor: DisplayDateAnchor | undefined): string {
    return anchor?.originTimeZone ? todayInTimeZone(anchor.originTimeZone) : getLocalToday()
}

export function useWidgetState(initial?: Partial<WidgetState>, anchor?: DisplayDateAnchor) {
    const initialRef = useRef(initial)
    const anchorRef = useRef(anchor)

    const [state, rawSetState] = useState<WidgetState>(() => {
        // Server-safe: defaults + initial props, anchored to the server-computed
        // `anchor.today` so SSR and first client render agree (no ambient clock at
        // render) — invariants.md#ssr-safe-rendered-dates. With no anchor the date
        // is left un-clamped rather than read from the clock; the post-mount
        // re-anchor below then applies the origin zone (rule 3) or the user's zone
        // (rule 4).
        return adjustDates({ ...defaultState, ...definedValues(initial) }, anchor?.today)
    })

    const setState = useCallback((updater: SetStateAction<WidgetState>) => {
        rawSetState((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater
            return enforceReturnDate(next)
        })
    }, [])

    // After mount, re-anchor to the client's effective "today" and merge any
    // persisted state. Runs unconditionally (not just when localStorage exists) so
    // rule 4 — the user's own zone winning on an originless surface — takes effect
    // even for a first-time visitor. Re-anchoring to the origin zone (rule 3) also
    // stops a sibling widget's persisted state from dragging this one to the browser
    // clock. The equality guard skips the re-render when nothing changed.
    useEffect(() => {
        const today = clientToday(anchorRef.current)
        const stored = readStoredState()
        const base = stored
            ? { ...defaultState, ...stored, ...definedValues(initialRef.current) }
            : { ...defaultState, ...definedValues(initialRef.current) }
        rawSetState((prev) => {
            const next = adjustDates(base, today)
            return widgetStateEqual(prev, next) ? prev : next
        })
    }, [])

    useEffect(() => {
        const entry: StoredWidgetState = {
            state,
            timestamp: Date.now(),
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
    }, [state])

    return [state, setState] as const
}
